import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeftRight, Boxes, Plus, Warehouse as WarehouseIcon } from 'lucide-react';
import {
  type MovementType,
  type StockLevel,
  type WarehouseLocation,
  createWarehouse,
  getStock,
  listWarehouses,
  recordMovement,
} from '@/api/warehouse';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';

export function WarehousePage() {
  const { t } = useTranslation();
  const [locations, setLocations] = useState<WarehouseLocation[]>([]);
  const [selected, setSelected] = useState<WarehouseLocation | null>(null);
  const [stock, setStock] = useState<StockLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showMove, setShowMove] = useState(false);

  const loadLocations = useCallback(() => {
    setLoading(true);
    listWarehouses()
      .then((rows) => {
        setLocations(rows);
        setSelected((cur) => cur ?? rows[0] ?? null);
      })
      .catch(() => setLocations([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const loadStock = useCallback((id: string) => {
    setStockLoading(true);
    getStock(id)
      .then(setStock)
      .catch(() => setStock([]))
      .finally(() => setStockLoading(false));
  }, []);

  useEffect(() => {
    if (selected) loadStock(selected.id);
    else setStock([]);
  }, [selected, loadStock]);

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">{t('warehouse.title')}</h1>
          <p className="text-sm text-[var(--color-lop-text-secondary)]">{t('warehouse.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowMove(true)}
            disabled={locations.length === 0}
            data-testid="warehouse-move"
          >
            <ArrowLeftRight className="mr-1 h-4 w-4" />
            {t('warehouse.recordMovement')}
          </Button>
          <Button size="sm" onClick={() => setShowNew(true)} data-testid="warehouse-new">
            <Plus className="mr-1 h-4 w-4" />
            {t('warehouse.newLocation')}
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-[260px_1fr]">
        <aside className="space-y-2">
          <h2 className="px-1 text-xs font-medium uppercase tracking-wide text-[var(--color-lop-text-muted)]">
            {t('warehouse.locations')}
          </h2>
          {loading && <p className="px-1 text-sm text-muted-foreground">{t('common.loading')}</p>}
          {!loading && locations.length === 0 && (
            <p className="px-1 text-sm text-muted-foreground">{t('warehouse.empty')}</p>
          )}
          <ul className="space-y-1">
            {locations.map((loc) => (
              <li key={loc.id}>
                <button
                  type="button"
                  onClick={() => setSelected(loc)}
                  data-testid="warehouse-location"
                  className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm ${
                    selected?.id === loc.id
                      ? 'bg-[var(--color-lop-accent)]/15 text-[var(--color-lop-text-primary)]'
                      : 'text-[var(--color-lop-text-secondary)] hover:bg-white/5'
                  }`}
                >
                  <WarehouseIcon className="h-4 w-4 shrink-0 text-[var(--color-lop-text-muted)]" />
                  <span className="min-w-0 flex-1 truncate">{loc.name}</span>
                  <span className="text-xs text-[var(--color-lop-text-muted)]">{loc.code}</span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        <section className="overflow-hidden rounded-lg border bg-card">
          {!selected ? (
            <p className="p-6 text-center text-sm text-muted-foreground">{t('warehouse.selectPrompt')}</p>
          ) : (
            <>
              <div className="border-b px-4 py-3">
                <p className="font-medium text-[var(--color-lop-text-primary)]">{selected.name}</p>
                <p className="text-xs text-[var(--color-lop-text-muted)]">
                  {selected.address || t('warehouse.noAddress')}
                  {selected.countryCode ? ` · ${selected.countryCode}` : ''}
                </p>
              </div>
              {stockLoading && <p className="p-6 text-center text-sm text-muted-foreground">{t('common.loading')}</p>}
              {!stockLoading && stock.length === 0 && (
                <p className="flex flex-col items-center gap-2 p-8 text-center text-sm text-muted-foreground">
                  <Boxes className="h-8 w-8 opacity-40" />
                  {t('warehouse.noStock')}
                </p>
              )}
              {!stockLoading && stock.length > 0 && (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-xs text-[var(--color-lop-text-muted)]">
                      <th className="px-4 py-2 font-medium">{t('warehouse.col.variation')}</th>
                      <th className="px-4 py-2 text-right font-medium">{t('warehouse.col.onHand')}</th>
                      <th className="px-4 py-2 text-right font-medium">{t('warehouse.col.reserved')}</th>
                      <th className="px-4 py-2 text-right font-medium">{t('warehouse.col.available')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-lop-border)]">
                    {stock.map((s) => (
                      <tr key={s.variationId} data-testid="warehouse-stock-row">
                        <td className="px-4 py-3 font-mono text-xs text-[var(--color-lop-text-secondary)]">
                          {s.variationId.slice(0, 8)}
                        </td>
                        <td className="px-4 py-3 text-right text-[var(--color-lop-text-primary)]">{s.quantity}</td>
                        <td className="px-4 py-3 text-right text-[var(--color-lop-text-secondary)]">{s.reserved}</td>
                        <td className="px-4 py-3 text-right font-medium text-[var(--color-lop-text-primary)]">
                          {s.quantity - s.reserved}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </section>
      </div>

      {showNew && (
        <NewLocationModal
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            loadLocations();
          }}
        />
      )}
      {showMove && (
        <MovementModal
          locations={locations}
          defaultWarehouseId={selected?.id}
          onClose={() => setShowMove(false)}
          onDone={() => {
            setShowMove(false);
            if (selected) loadStock(selected.id);
          }}
        />
      )}
    </div>
  );
}

function NewLocationModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!code.trim() || !name.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await createWarehouse({
        code: code.trim(),
        name: name.trim(),
        address: address.trim() || undefined,
        countryCode: countryCode.trim() || undefined,
      });
      onCreated();
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to create location');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('warehouse.newModal.title')}</h3>
        {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('warehouse.newModal.code')}
            </label>
            <Input value={code} onChange={(e) => setCode(e.target.value)} data-testid="warehouse-code" />
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('warehouse.newModal.country')}
            </label>
            <Input value={countryCode} onChange={(e) => setCountryCode(e.target.value)} maxLength={2} />
          </div>
        </div>
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('warehouse.newModal.name')}
        </label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="mb-3" data-testid="warehouse-name" />
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('warehouse.newModal.address')}
        </label>
        <Input value={address} onChange={(e) => setAddress(e.target.value)} className="mb-4" />
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting} data-testid="warehouse-submit">
            {t('common.create')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function MovementModal({
  locations,
  defaultWarehouseId,
  onClose,
  onDone,
}: {
  locations: WarehouseLocation[];
  defaultWarehouseId?: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [warehouseId, setWarehouseId] = useState(defaultWarehouseId ?? locations[0]?.id ?? '');
  const [variationId, setVariationId] = useState('');
  const [movementType, setMovementType] = useState<MovementType>('incoming');
  const [quantity, setQuantity] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const qty = Number(quantity);
    if (!warehouseId || !variationId.trim() || !Number.isFinite(qty) || qty <= 0) {
      setError(t('warehouse.moveModal.invalid'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await recordMovement({
        warehouseId,
        variationId: variationId.trim(),
        movementType,
        quantity: qty,
        reference: reference.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      onDone();
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to record movement');
    } finally {
      setSubmitting(false);
    }
  }

  const types: MovementType[] = ['incoming', 'outgoing', 'transfer_out', 'adjustment'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('warehouse.moveModal.title')}</h3>
        {error && <p className="mb-3 text-sm text-red-400">{error}</p>}
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('warehouse.moveModal.warehouse')}
        </label>
        <Select
          value={warehouseId}
          onChange={(e) => setWarehouseId(e.target.value)}
          className="mb-3"
          data-testid="warehouse-move-location"
        >
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </Select>
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('warehouse.moveModal.variation')}
        </label>
        <Input
          value={variationId}
          onChange={(e) => setVariationId(e.target.value)}
          placeholder={t('warehouse.moveModal.variationPlaceholder')}
          className="mb-3 font-mono"
          data-testid="warehouse-move-variation"
        />
        <div className="mb-3 grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('warehouse.moveModal.type')}
            </label>
            <Select
              value={movementType}
              onChange={(e) => setMovementType(e.target.value as MovementType)}
              data-testid="warehouse-move-type"
            >
              {types.map((ty) => (
                <option key={ty} value={ty}>
                  {t(`warehouse.movementType.${ty}`)}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
              {t('warehouse.moveModal.quantity')}
            </label>
            <Input
              type="number"
              min="0"
              step="1"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              data-testid="warehouse-move-quantity"
            />
          </div>
        </div>
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('warehouse.moveModal.reference')}
        </label>
        <Input value={reference} onChange={(e) => setReference(e.target.value)} className="mb-3" />
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('warehouse.moveModal.notes')}
        </label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mb-4" />
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting} data-testid="warehouse-move-submit">
            {t('warehouse.moveModal.submit')}
          </Button>
        </div>
      </div>
    </div>
  );
}
